<template>
  <v-container>
    <!-- 1. Loader d’initialisation -->
    <div
      v-if="loadingChat"
      class="loader-container"
    >
      <v-progress-circular
        indeterminate
        color="primary"
        size="40"
      />
      <p class="mt-4">{{ $t("commons.init_chat") }}</p>
    </div>

    <!-- 2. Erreur de connexion -->
    <div
      v-else-if="chatError"
      class="error-container"
    >
      <v-icon
        large
        color="error"
        >mdi-alert</v-icon
      >
      <p class="mt-2">{{ $t("commons.echec_start") }}</p>
      <v-btn
        color="primary"
        @click="initChat"
        class="mt-4"
        >{{ $t("commons.retry") }}</v-btn
      >
    </div>
    <!-- ✅ Message temporaire de succès -->
    <div
      v-else-if="showSuccessMessage"
      class="success-container"
    >
      <v-icon
        large
        color="success"
        >mdi-check-circle</v-icon
      >
      <p class="mt-2">{{ $t("commons.chat_connected") }}</p>
    </div>

    <!-- 3. Chat prêt -->
    <div v-else-if="chatReady">
      <avs-card
        :noTitle="true"
        class="chat-window"
      >
        <template v-slot:content>
          <v-list>
            <div
              v-for="(message, index) in messages"
              :key="index"
              class="chat-message"
              :class="{ sent: message.sent, received: !message.sent }"
            >
              <div class="message-text mt-2">{{ message.text }}</div>
            </div>
          </v-list>
          <div
            v-if="isThinking"
            class="thinking-indicator"
          >
            <span class="dot"></span>
            <span class="dot"></span>
            <span class="dot"></span>
          </div>
        </template>
      </avs-card>

      <!-- Input Area -->
      <v-row class="mt-5">
        <v-col cols="10">
          <v-text-field
            v-model="newMessage"
            :label="$t('commons.type_message')"
            outlined
            dense
            @keyup.enter="sendMessage"
          ></v-text-field>
        </v-col>
        <v-col cols="2">
          <v-btn
            @click="sendMessage"
            color="primary"
            block
            >{{ $t("commons.send") }}</v-btn
          >
        </v-col>
      </v-row>
    </div>
  </v-container>
</template>

<script>
import { getTheme } from "@/plugins/vuetify"
import IaService from "@/services/IaService"
export default {
  name: "ChatIA",
  data() {
    return {
      newMessage: "",
      messages: [
        { text: "Hello! How can I help you?", sent: false },
        { text: "I am looking for a Vue.js chat interface.", sent: true }
      ],
      host: null,
      port: null,
      isThinking: false,
      loadingChat: true, // ⏳ État de chargement initial
      chatReady: false, // ✅ Quand c’est OK
      chatError: false, // ❌ Si erreur
      showSuccessMessage: false
    }
  },
  // mounted() {
  //   const storedMessages = localStorage.getItem("ia_chat_messages")
  //   if (storedMessages) {
  //     this.messages = JSON.parse(storedMessages)
  //   }
  //   this.initChat()
  // },
  mounted() {
    const projectKey = `ia_chat_messages_${this.projectId}`
    const storedMessages = localStorage.getItem(projectKey)

    if (storedMessages) {
      this.messages = JSON.parse(storedMessages)
    } else {
      this.clearChat()
    }

    this.initChat()
  },
  watch: {
    projectId(newVal, oldVal) {
      if (newVal !== oldVal) {
        this.clearChat()
        this.initChat()
      }
    }
  },
  methods: {
    startChat() {
      IaService.startChat(this.companyId, this.projectId).then(res => {
        this.host = res.host
        this.port = res.port
      })
    },
    initChat() {
      this.loadingChat = true
      this.chatReady = false
      this.chatError = false

      IaService.startChat(this.companyId, this.projectId)
        .then(res => {
          this.host = res.host
          this.port = res.port

          // ✅ Affichage succès après une petite pause
          this.loadingChat = false
          this.showSuccessMessage = true
          setTimeout(() => {
            this.showSuccessMessage = false
            this.loadingChat = false
            this.chatReady = true
          }, 1000) // 1 seconde pour laisser le check vert visible
        })
        .catch(() => {
          this.loadingChat = false
          this.chatError = true
        })
    },
    // saveMessages() {
    //   localStorage.setItem("ia_chat_messages", JSON.stringify(this.messages))
    // },
    saveMessages() {
      const projectKey = `ia_chat_messages_${this.projectId}`
      localStorage.setItem(projectKey, JSON.stringify(this.messages))
    },
    // clearChat() {
    //   this.messages = []
    //   localStorage.removeItem("ia_chat_messages")
    // },
    clearChat() {
      const projectKey = `ia_chat_messages_${this.projectId}`
      this.messages = []
      localStorage.removeItem(projectKey)
    },
    sendMessage() {
      if (!this.newMessage.trim()) return

      this.messages.push({ text: this.newMessage, sent: true })
      this.saveMessages()
      this.isThinking = true

      IaService.sendMessage(this, this.companyId, this.projectId, {
        host: this.host,
        port: this.port,
        message: this.newMessage
      }).then(res => {
        this.isThinking = false
        this.messages.push({ text: res.response, sent: false })
        this.saveMessages()
        // Scroll vers le bas après réception
        this.$nextTick(() => {
          const container = this.$el.querySelector(".chat-window")
          container.scrollTop = container.scrollHeight
        })
      })

      this.newMessage = ""
    }
  },
  computed: {
    companyId() {
      return this.$store.state.app.company?.pk
    },
    cardStyle() {
      return { backgroundColor: getTheme(this).bgCard }
    },
    projectId() {
      return this.$store.state.app.project?.id
    }
  }
}
</script>

<style scoped>
.chat-window {
  overflow-y: auto;
  background-color: #f5f5f5;
  padding: 10px;
}
.chat-window {
  overflow-y: auto;
  background-color: #f5f5f5;
  padding: 10px;
  max-height: 500px;
  border-radius: 10px;
}

/* Container flex pour gérer alignement horizontal */
.chat-message {
  display: flex;
  width: 100%;
}

/* aligner le message à droite */
.sent {
  justify-content: flex-end;
}

/* aligner le message à gauche */
.received {
  justify-content: flex-start;
}

/* Style du bulle de message */
.message-text {
  max-width: 75%;
  padding: 10px 15px;
  border-radius: 12px;
  font-size: 14px;
  white-space: pre-line;
  word-break: break-word;
}

/* Style message utilisateur */
.sent .message-text {
  background-color: #d1f4ff;
  color: #000;
  border-top-right-radius: 0;
  text-align: right;
}

/* Style message IA */
.received .message-text {
  background-color: #e0e0e0;
  color: #000;
  border-top-left-radius: 0;
  text-align: left;
}

.thinking-indicator {
  display: flex;
  justify-content: flex-start;
  align-items: center;
  padding-left: 10px;
  height: 30px;
  margin-top: 5px;
}

.dot {
  height: 10px;
  width: 10px;
  margin: 0 2px;
  background-color: #888;
  border-radius: 50%;
  display: inline-block;
  animation: blink 1.4s infinite both;
}

.dot:nth-child(2) {
  animation-delay: 0.2s;
}
.dot:nth-child(3) {
  animation-delay: 0.4s;
}

@keyframes blink {
  0%,
  80%,
  100% {
    opacity: 0;
  }
  40% {
    opacity: 1;
  }
}

.loader-container,
.error-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 200px;
  text-align: center;
  color: #444;
}

.loader-container p,
.error-container p {
  margin-top: 12px;
  font-size: 16px;
}

.success-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 200px;
  text-align: center;
  color: #4caf50;
}

/*
.sent {
  justify-content: flex-end;
}

.received {
  justify-content: flex-start;
}

.v-list-item {
  display: flex;
  margin-bottom: 10px;
}

.v-list-item-title {
  padding: 10px;
  border-radius: 8px;
}

.sent .v-list-item-title {
  background-color: #d1f4ff;
}

.received .v-list-item-title {
  background-color: #e0e0e0;
}*/
</style>
